import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { isNativeEnvironment } from '@/hooks/useNativeApp';

interface SubscriptionContextType {
  subscribed: boolean;
  subscriptionTier: string;
  subscriptionEnd: string | null;
  loading: boolean;
  checkSubscription: () => Promise<void>;
  createCheckout: (plan?: string) => Promise<void>;
  openCustomerPortal: () => Promise<void>;
  dailyUsage: {
    ai_prompts_used: number;
    remaining_prompts: number;
  };
  canUseFeature: (feature: string) => boolean;
  incrementUsage: (feature: string) => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const { user, session } = useAuth();
  const { toast } = useToast();
  const [subscribed, setSubscribed] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState('free');
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [dailyUsage, setDailyUsage] = useState({
    ai_prompts_used: 0,
    remaining_prompts: 2, // weekly limit for free tier
  });

  // Check subscription status
  const checkSubscription = async () => {
    if (!user || !session) return;

    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      setSubscribed(data.subscribed || false);
      setSubscriptionTier(data.subscription_tier || 'free');
      setSubscriptionEnd(data.subscription_end);

      // Load weekly usage if on free tier
      if (data.subscription_tier === 'free') {
        await loadWeeklyUsage();
      }
    } catch (error: any) {
      console.error('Error checking subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get start of current week (Monday)
  const getWeekStart = () => {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Monday
    const monday = new Date(now);
    monday.setDate(diff);
    return monday.toISOString().split('T')[0];
  };

  // Load weekly usage for free tier users
  const loadWeeklyUsage = async () => {
    if (!user) return;

    try {
      const weekStart = getWeekStart();
      const today = new Date().toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('daily_usage')
        .select('ai_prompts_used')
        .eq('user_id', user.id)
        .eq('feature_type', 'ai_prompts')
        .gte('usage_date', weekStart)
        .lte('usage_date', today);

      if (error) {
        console.error('Error loading weekly usage:', error);
        return;
      }

      const used = (data || []).reduce((sum, row) => sum + (row.ai_prompts_used || 0), 0);
      setDailyUsage({
        ai_prompts_used: used,
        remaining_prompts: Math.max(0, 2 - used),
      });
    } catch (error) {
      console.error('Error loading weekly usage:', error);
    }
  };

  // Create checkout session
  const createCheckout = async (plan = 'monthly') => {
    if (!session) return;

    try {
      setLoading(true);
      
      // Refresh session before making checkout call to ensure valid JWT
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError || !refreshData.session) {
        toast({
          title: "Session Expired",
          description: "Please sign in again to continue.",
          variant: "destructive",
        });
        window.location.href = '/auth';
        return;
      }
      
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { plan },
        headers: {
          Authorization: `Bearer ${refreshData.session.access_token}`,
        },
      });

      if (error) {
        if (error.message?.includes('native app') || error.message?.includes('App Store')) {
          window.location.href = '/native-purchase-restricted';
          return;
        }
        throw error;
      }

      window.location.href = data.url;
    } catch (error: any) {
      console.error('Error creating checkout:', error);
      
      if (error.message?.includes('Session') || error.message?.includes('JWT') || error.message?.includes('Authentication')) {
        toast({
          title: "Session Expired",
          description: "Please sign in again to continue.",
          variant: "destructive",
        });
        window.location.href = '/auth';
        return;
      }
      
      toast({
        title: "Checkout Error",
        description: error.message || "Failed to create checkout session",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Open customer portal
  const openCustomerPortal = async () => {
    if (!session) return;

    try {
      setLoading(true);
      
      // Refresh session before making portal call
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError || !refreshData.session) {
        toast({
          title: "Session Expired",
          description: "Please sign in again to continue.",
          variant: "destructive",
        });
        window.location.href = '/auth';
        return;
      }
      
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        headers: {
          Authorization: `Bearer ${refreshData.session.access_token}`,
        },
      });

      if (error) {
        if (error.message?.includes('native app') || error.message?.includes('App Store')) {
          window.location.href = '/native-purchase-restricted';
          return;
        }
        throw error;
      }

      window.location.href = data.url;
    } catch (error: any) {
      console.error('Error opening customer portal:', error);
      
      if (error.message?.includes('Session') || error.message?.includes('JWT') || error.message?.includes('Authentication')) {
        toast({
          title: "Session Expired",
          description: "Please sign in again to continue.",
          variant: "destructive",
        });
        window.location.href = '/auth';
        return;
      }
      
      toast({
        title: "Portal Error",
        description: error.message || "Failed to open customer portal",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Check if user can use a feature
  const canUseFeature = (feature: string) => {
    if (subscriptionTier === 'pro' || subscriptionTier === 'ultra') return true;

    switch (feature) {
      case 'ai_prompts':
        return dailyUsage.remaining_prompts > 0;
      case 'ai_image_generation':
      case 'ai_meal_plan':
      case 'recipe_collections':
      case 'dietary_preferences':
        return false; // Pro/Ultra only
      default:
        return true; // Core features are free
    }
  };

  // Increment usage counter
  const incrementUsage = async (feature: string) => {
    if (!user || subscriptionTier === 'pro' || subscriptionTier === 'ultra') return;

    try {
      const today = new Date().toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from('daily_usage')
        .upsert({
          user_id: user.id,
          usage_date: today,
          feature_type: feature,
          ai_prompts_used: feature === 'ai_prompts' ? dailyUsage.ai_prompts_used + 1 : 0,
        }, {
          onConflict: 'user_id,usage_date,feature_type',
        });

      if (error) throw error;

      // Update local state
      if (feature === 'ai_prompts') {
        setDailyUsage(prev => ({
          ai_prompts_used: prev.ai_prompts_used + 1,
          remaining_prompts: Math.max(0, prev.remaining_prompts - 1),
        }));
      }
    } catch (error) {
      console.error('Error incrementing usage:', error);
    }
  };

  // Check subscription on auth changes
  useEffect(() => {
    if (user && session) {
      checkSubscription();
    } else {
      // Reset state when user logs out
      setSubscribed(false);
      setSubscriptionTier('free');
      setSubscriptionEnd(null);
      setDailyUsage({ ai_prompts_used: 0, remaining_prompts: 2 });
    }
  }, [user, session]);

  // Refresh subscription status every 10 seconds when user is on success page
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true' && user && session) {
      const interval = setInterval(checkSubscription, 10000);
      return () => clearInterval(interval);
    }
  }, [user, session]);

  const value = {
    subscribed,
    subscriptionTier,
    subscriptionEnd,
    loading,
    checkSubscription,
    createCheckout,
    openCustomerPortal,
    dailyUsage,
    canUseFeature,
    incrementUsage,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};